import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { apiClient } from '../../services/apiClient';
import { clabsApiClient } from '../../services/clabsApiService';
import KnowledgeBaseModal from '../modals/KnowledgeModal/KnowledgeModal';
import ClabsModal from '../modals/ClabsModal/ClabsModal';
import PageHeader from '../common/PageHeader/PageHeader';
import Button from '../common/Button/Button';
import Badge from '../common/Badge/Badge';
import Input from '../common/Input/Input';
import Select from '../common/Select/Select';
import ToggleSwitch from '../common/ToggleSwitch/ToggleSwitch';
import './InformationSources.css';
import CustomerDatabaseModal from '../modals/CustomerDatabaseModal/CustomerDatabaseModal';
import PersonalContextModal from '../modals/PersonalContextModal/PersonalContextModal';
import PortalModal from '../common/PortalModal/PortalModal';
import { isSourceVisible } from '../../config/sourcesConfig';
import { config } from '../../config/envConfig';
import { useAppContext } from '../../context/AppContext';

export default function InformationSources() {
  const { defaultModelFor } = useAppContext();
  const REGISTRY_OPENAI_EMBEDDING_DEFAULT = defaultModelFor('openai', 'embedding') || 'text-embedding-ada-002';
  const defaultWebSearchSettings = {
    url: 'http://localhost:8080',
    enabled: true,
    search_engines: ['google', 'bing'],
    language: 'all',
    deep_research_mode: false,
    max_results: 5
  };

  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [searchTerm] = useState('');
  const [filterType] = useState('all');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [showWebSearchConfigModal, setShowWebSearchConfigModal] = useState(false);
  const [webSearchSettings, setWebSearchSettings] = useState(defaultWebSearchSettings);
  const [_availableGroups, setAvailableGroups] = useState([]);
  const [uploadAccessLevel, _setUploadAccessLevel] = useState('private');
  const [selectedUploadGroups, _setSelectedUploadGroups] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [uploadWorkspace, setUploadWorkspace] = useState('shared');

  // Knowledge Base modal state
  const [showKnowledgeBaseModal, setShowKnowledgeBaseModal] = useState(false);
  const [bedrockConfig, setBedrockConfig] = useState({
    is_configured: false,
    knowledge_base_id: '',
    region: 'us-east-1',
    has_credentials: false,
    client_status: 'not_initialized'
  });

  // Clabs modal state
  const [showClabsModal, setShowClabsModal] = useState(false);
  const [editingClabsSource, setEditingClabsSource] = useState(null);
  const [storedClabsConfigs, setStoredClabsConfigs] = useState([]);

  // Clabs-specific state (moved to modal)
  const [clabsSystemSource, setClabsSystemSource] = useState({
    id: 'clabs-system',
    name: 'Clabs Source Store',
    type: 'clabs',
    description: 'Connect to your Clabs instance to access cluster-based knowledge sources',
    clabsConfig: {
      apiUrl: config.CLABS_API_URL,
      selectedSources: [],
      availableSources: [],
      enabled: true
    },
    accessLevel: 'private',
    tags: ['clabs', 'clusters'],
    isActive: false,
    isSystemIntegration: true,
    integrationStatus: 'not_configured',
    syncFrequency: 'real-time',
    lastSync: null,
    createdAt: '2024-01-10T00:00:00Z',
    updatedAt: '2024-01-10T00:00:00Z'
  });
  // Customer Database modal state
  const [showCustomerDatabaseModal, setShowCustomerDatabaseModal] = useState(false);
  const [customerDatabaseConfig, setCustomerDatabaseConfig] = useState(null);
  
  // Personal Context modal state
  const [showPersonalContextModal, setShowPersonalContextModal] = useState(false);

  // RAG Configuration modal state
  const [showRAGConfigModal, setShowRAGConfigModal] = useState(false);
  const [ragConfigTab, setRagConfigTab] = useState('files'); // 'files' or 'settings'
  const [deletingAllFiles, setDeletingAllFiles] = useState(false);
  const [uploadPhase, setUploadPhase] = useState('idle'); // idle | selecting | preparing | uploading
  const [selectedUploadCount, setSelectedUploadCount] = useState(0);
  const [isCancellingUpload, setIsCancellingUpload] = useState(false);
  const [isDragOverUpload, setIsDragOverUpload] = useState(false);
  const uploadControllersRef = useRef([]);
  const fileInputRef = useRef(null);
  const pendingFileDialogRef = useRef(false);
  const dragCounterRef = useRef(0);
  const RAG_CONFIG_DEFAULTS = {
    chunk_size: 400,
    chunk_overlap: 50,
    parent_chunk_size: 2000,
    parent_chunk_overlap: 200,
    top_k_results: 5,
    similarity_threshold: 0.1,
    embedding_model: REGISTRY_OPENAI_EMBEDDING_DEFAULT
  };
  const [ragConfig, setRagConfig] = useState(RAG_CONFIG_DEFAULTS);
  // Working copy bound to Settings-tab inputs; reset to ragConfig after save.
  const [ragConfigDraft, setRagConfigDraft] = useState(RAG_CONFIG_DEFAULTS);
  const [savingRagConfig, setSavingRagConfig] = useState(false);
  const [ragConfigSaveError, setRagConfigSaveError] = useState(null);
  const [ragConfigSavedFlash, setRagConfigSavedFlash] = useState(false);

  // Per-upload chunk overrides (defaults seeded from server config).
  const [chunkOverridesOpen, setChunkOverridesOpen] = useState(false);
  const [chunkOverrides, setChunkOverrides] = useState({
    parent_chunk_size: 2000,
    parent_chunk_overlap: 200,
    child_chunk_size: 400,
    child_chunk_overlap: 50
  });

  // RAG system state
  const [ragDocuments, setRagDocuments] = useState([]);
  const [ragStats, setRagStats] = useState({
    total_documents: 0,
    total_chunks: 0,
    average_chunks_per_document: 0
  });

  // Form state for creating/editing sources
  const [formData, setFormData] = useState({
    name: '',
    type: 'database',
    description: '',
    connectionString: '',
    apiEndpoint: '',
    clabsConfig: {
      apiUrl: '',
      selectedSources: [],
      availableSources: []
    },
    authentication: {
      type: 'none',
      username: '',
      password: '',
      apiKey: '',
      token: ''
    },
    accessLevel: 'private',
    tags: [],
    isActive: true,
    syncFrequency: 'daily',
    lastSync: null,
    uploadedFiles: []
  });

  useEffect(() => {
    loadInformationSources();
    loadBedrockConfig();
    loadClabsConfiguration();
    loadRAGDocuments();
    loadRAGConfig();
    loadAvailableGroups();
    loadWorkspaces();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showRAGConfigModal) {
      loadRAGDocuments();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadWorkspace, showRAGConfigModal]);

  const loadBedrockConfig = async () => {
    try {
      // apiClient.request() already returns parsed JSON
      const config = await apiClient.request('/bedrock/config', {
        method: 'GET'
      });

      console.log('Loaded Bedrock config:', config);
      setBedrockConfig(config);
    } catch (error) {
      console.error('Error loading Bedrock config:', error);
    }
  };

  const loadRAGConfig = async () => {
    try {
      const response = await apiClient.request('/rag/config', {
        method: 'GET'
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Loaded RAG config:', data);

        // Update state with server configuration
        if (data.status === 'success') {
          const limits = data.limits || {};
          const nextConfig = {
            chunk_size: limits.chunk_size ?? 400,
            chunk_overlap: limits.chunk_overlap ?? 50,
            parent_chunk_size: limits.parent_chunk_size ?? 2000,
            parent_chunk_overlap: limits.parent_chunk_overlap ?? 200,
            top_k_results: data.search?.top_k_results || 5,
            similarity_threshold: data.search?.similarity_threshold || 0.1,
            embedding_model: data.processing?.embedding_model || REGISTRY_OPENAI_EMBEDDING_DEFAULT
          };
          setRagConfig(nextConfig);
          setRagConfigDraft(nextConfig);
          setChunkOverrides({
            parent_chunk_size: nextConfig.parent_chunk_size,
            parent_chunk_overlap: nextConfig.parent_chunk_overlap,
            child_chunk_size: nextConfig.chunk_size,
            child_chunk_overlap: nextConfig.chunk_overlap
          });
        }
      }
    } catch (error) {
      console.error('Error loading RAG config:', error);
    }
  };

  const loadAvailableGroups = async () => {
    try {
      const response = await apiClient.request('/organization/groups', { method: 'GET' });
      const groups = response?.groups || [];
      setAvailableGroups(groups);
    } catch (error) {
      console.error('Error loading organization groups:', error);
      setAvailableGroups([]);
    }
  };

  const loadWorkspaces = async () => {
    try {
      const response = await apiClient.getWorkspaces();
      setWorkspaces(response?.workspaces || []);
    } catch (error) {
      console.error('Error loading workspaces:', error);
      setWorkspaces([]);
    }
  };

  const loadClabsConfiguration = async () => {
    try {
      const clabsConfig = await apiClient.getClabsConfig();

      // Update the system Clabs source based on user's configuration
      if (clabsConfig) {
        setClabsSystemSource(prev => ({
          ...prev,
          name: 'Clabs Source Store',
          description: clabsConfig.description || 'Connected to your Clabs instance',
          clabsConfig: {
            id: clabsConfig.id,
            apiUrl: clabsConfig.api_url,
            selectedSources: clabsConfig.selected_sources || [],
            availableSources: clabsConfig.available_sources || [],
            enabled: typeof clabsConfig.enabled === 'boolean' ? clabsConfig.enabled : true
          },
          isActive: clabsConfig.is_active,
          integrationStatus: clabsConfig.connection_status === 'connected' ? 'configured' : 'not_configured',
          updatedAt: clabsConfig.updated_at
        }));
        setStoredClabsConfigs([clabsConfig]); // Keep as array for compatibility
      } else {
        // Reset to default state if no configuration
        setClabsSystemSource(prev => ({
          ...prev,
          name: 'Clabs Source Store',
          description: 'Connect to your Clabs instance to access cluster-based knowledge sources',
          clabsConfig: {
            apiUrl: config.CLABS_API_URL,
            selectedSources: [],
            availableSources: [],
            enabled: true
          },
          isActive: false,
          integrationStatus: 'not_configured'
        }));
        setStoredClabsConfigs([]);
      }
    } catch (error) {
      console.error('Error loading Clabs configuration:', error);
      setStoredClabsConfigs([]);
    }
  };

  const loadInformationSources = async () => {
    setLoading(true);
    try {
      // Load RAG documents first to get actual files
      const ragDocs = await loadRAGDocuments();
      let webSearchSource = {
        id: 'web_search',
        name: 'Web Search (SearXNG)',
        type: 'web_scraper',
        description: 'Real-time internet access.',
        accessLevel: 'public',
        tags: ['internet'],
        isActive: false,
        isConfigured: false,
        integrationStatus: 'not_configured',
        syncFrequency: 'real-time',
        lastSync: null,
        createdAt: null,
        updatedAt: new Date().toISOString()
      };
      try {
        const configResponse = await apiClient.getWebSearchConfig();
        if (configResponse) {
          const normalizedSettings = {
            ...defaultWebSearchSettings,
            ...configResponse,
            search_engines: configResponse.search_engines || configResponse.engines || defaultWebSearchSettings.search_engines
          };
          webSearchSource = {
            ...webSearchSource,
            isActive: normalizedSettings.enabled,
            isConfigured: true,
            integrationStatus: normalizedSettings.enabled ? 'active' : 'disabled'
          };
          setWebSearchSettings(normalizedSettings);
        }
      } catch (error) {
        console.warn('Web search config not found, showing default');
      }

      // Load RAG enabled state from backend (falls back to localStorage)
      let isRAGEnabled = false;
      try {
        const config = await apiClient.getRagIntegrationConfig();
        const configData = config?.config || config;
        if (configData && typeof configData.enabled === 'boolean') {
          isRAGEnabled = configData.enabled;
          localStorage.setItem('ragEnabled', JSON.stringify(isRAGEnabled));
        } else {
          const ragEnabledFromStorage = localStorage.getItem('ragEnabled');
          isRAGEnabled = ragEnabledFromStorage ? JSON.parse(ragEnabledFromStorage) : false;
        }
      } catch (e) {
        const ragEnabledFromStorage = localStorage.getItem('ragEnabled');
        isRAGEnabled = ragEnabledFromStorage ? JSON.parse(ragEnabledFromStorage) : false;
      }

      // Create sources with RAG integration
      // Load only non-Clabs sources for now - Clabs sources will be populated when users configure them
      const baseSources = [
        {
          id: 2,
          name: 'Customer Database',
          type: 'database',
          description: 'Customer information and transaction history',
          connectionString: 'postgresql://customer-db:5432/customers',
          accessLevel: 'restricted',
          tags: [],
          isActive: false,
          isCorporateIntegration: true,
          integrationStatus: 'available',
          syncFrequency: 'real-time',
          lastSync: '2024-01-15T11:45:00Z',
          createdAt: '2024-01-02T00:00:00Z',
          updatedAt: '2024-01-15T11:45:00Z'
        },
        {
          id: 1,
          name: 'Internal Knowledge Base',
          type: 'knowledge_base',
          description: 'Company internal documentation and procedures powered by RAG',
          connectionString: 'mongodb://internal-kb:27017/knowledge',
          accessLevel: 'private',
          tags: [],
          isActive: true,
          ragEnabled: isRAGEnabled, // Initialize from localStorage
          isCorporateIntegration: true,
          integrationStatus: 'available', // Changed from coming_soon
          syncFrequency: 'real-time',
          lastSync: ragDocs.length > 0 ? new Date().toISOString() : '2024-01-15T10:30:00Z',
          createdAt: '2024-01-01T00:00:00Z',
          updatedAt: new Date().toISOString(),
          uploadedFiles: ragDocs.map(doc => ({
            id: doc.document_id,
            name: doc.filename,
            size: doc.file_size,
            type: doc.content_type,
            uploadDate: doc.upload_date,
            status: 'processed',
            chunks: doc.total_chunks
          }))
        }
      ];
      const combinedSources = [...baseSources, webSearchSource];
      setSources(combinedSources);

      console.log('Updated sources with RAG documents:', ragDocs.length);
      console.log('Internal Knowledge Base isActive:', baseSources.find(s => s.name === 'Internal Knowledge Base')?.isActive);
    } catch (error) {
      console.error('Error loading information sources:', error);
    } finally {
      setLoading(false);
    }
  };

  const sourceTypes = [
    { value: 'database', label: 'Database', icon: 'fas fa-database' },
    { value: 'api', label: 'API Endpoint', icon: 'fas fa-plug' },
    { value: 'knowledge_base', label: 'Knowledge Base', icon: 'fas fa-book' },
    { value: 'clabs', label: 'Clabs Source Store', icon: 'fas fa-brain' },
    { value: 'file_system', label: 'File System', icon: 'fas fa-folder' },
    { value: 'web_scraper', label: 'Web Scraper', icon: 'fas fa-globe' },
    { value: 'document_store', label: 'Document Store', icon: 'fas fa-file-alt' },
    { value: 'jira', label: 'Jira Corporate', icon: 'fab fa-jira', disabled: true },
    { value: 'github', label: 'GitHub Corporate', icon: 'fab fa-github', disabled: true }
  ];

  const accessLevels = [
    { value: 'public', label: 'Public', color: 'green' },
    { value: 'private', label: 'Private', color: 'blue' },
    { value: 'restricted', label: 'Restricted', color: 'orange' },
    { value: 'confidential', label: 'Confidential', color: 'red' }
  ];

  const syncFrequencies = [
    { value: 'real-time', label: 'Real-time' },
    { value: 'hourly', label: 'Hourly' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'manual', label: 'Manual' }
  ];

  const webSearchEngineOptions = [
    { id: 'google', label: 'Google' },
    { id: 'bing', label: 'Bing' },
    { id: 'duckduckgo', label: 'DuckDuckGo' },
    { id: 'wikipedia', label: 'Wikipedia' }
  ];

  const handleCreateSource = () => {
    setIsCreating(true);
    setEditingSource(null);
    setFormData({
      name: '',
      type: 'database',
      description: '',
      connectionString: '',
      apiEndpoint: '',
      clabsConfig: {
        apiUrl: clabsApiClient.api_url,
        selectedSources: [],
        availableSources: []
      },
      authentication: {
        type: 'none',
        username: '',
        password: '',
        apiKey: '',
        token: ''
      },
      accessLevel: 'private',
      tags: [],
      isActive: true,
      syncFrequency: 'daily',
      lastSync: null,
      uploadedFiles: []
    });
  };

  const handleEditSource = (source) => {
    if (source.type === 'clabs') {
      // For system Clabs source, find the first stored configuration or create new
      if (source.id === 'clabs-system' && storedClabsConfigs.length > 0) {
        const activeConfig = storedClabsConfigs.find(config => config.is_active) || storedClabsConfigs[0];
        setEditingClabsSource({
          ...source,
          ...activeConfig, // Pass the stored configuration data
          // Convert backend format to frontend format
          name: activeConfig.name,
          apiUrl: activeConfig.api_url,
          selectedSources: activeConfig.selected_sources || [],
          availableSources: activeConfig.available_sources || [],
          enabled: typeof activeConfig.enabled === 'boolean' ? activeConfig.enabled : true
        });
      } else {
        setEditingClabsSource(source);
      }
      setShowClabsModal(true);
    } else {
      setEditingSource(source);
      setIsCreating(false);
      setFormData({
        ...source,
        clabsConfig: source.clabsConfig || {
          apiUrl: clabsApiClient.api_url,
          selectedSources: [],
          availableSources: []
        },
        authentication: source.authentication || {
          type: 'none',
          username: '',
          password: '',
          apiKey: '',
          token: ''
        }
      });
    }
  };

  const handleCreateClabsSource = () => {
    setEditingClabsSource(null);
    setShowClabsModal(true);
  };

  const handleSaveClabsConfiguration = async (clabsConfig) => {
    try {
      if (editingClabsSource) {
        // Update existing Clabs source
        if (editingClabsSource.id === 'clabs-system') {
          // Configuration was saved to backend, reload configurations to reflect changes
          await loadClabsConfiguration();
        } else {
          const updatedSources = sources.map(source =>
            source.id === editingClabsSource.id
              ? {
                ...source,
                clabsConfig: {
                  apiUrl: clabsConfig.apiUrl,
                  selectedSources: clabsConfig.selectedSources,
                  availableSources: clabsConfig.availableSources
                },
                isActive: true,
                integrationStatus: 'configured',
                updatedAt: new Date().toISOString()
              }
              : source
          );
          setSources(updatedSources);
        }
      } else {
        // Configuration was saved to backend, reload configurations to reflect changes
        await loadClabsConfiguration();
      }

      // Always reload the Clabs configuration to get the latest state
      await loadClabsConfiguration();

      setEditingClabsSource(null);
      setShowClabsModal(false);
    } catch (error) {
      console.error('Error handling Clabs configuration save:', error);
      alert('Error saving Clabs configuration: ' + error.message);
    }
  };



  const handleSaveSource = async () => {
    try {
      if (editingSource) {
        // Update existing source
        if (editingSource.id === 'clabs-system') {
          setClabsSystemSource(prev => ({
            ...prev,
            ...formData,
            updatedAt: new Date().toISOString()
          }));
        } else {
          const updatedSources = sources.map(source =>
            source.id === editingSource.id
              ? { ...formData, id: editingSource.id, updatedAt: new Date().toISOString() }
              : source
          );
          setSources(updatedSources);
        }
        setEditingSource(null);
      } else {
        // Create new source
        const newSource = {
          ...formData,
          id: Date.now(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        setSources([newSource, ...sources]);
        setIsCreating(false);
      }

      // Reset form
      setFormData({
        name: '',
        type: 'database',
        description: '',
        connectionString: '',
        apiEndpoint: '',
        clabsConfig: {
          apiUrl: clabsApiClient.api_url,
          selectedSources: [],
          availableSources: []
        },
        authentication: {
          type: 'none',
          username: '',
          password: '',
          apiKey: '',
          token: ''
        },
        accessLevel: 'private',
        tags: [],
        isActive: true,
        syncFrequency: 'daily',
        lastSync: null,
        uploadedFiles: []
      });
    } catch (error) {
      console.error('Error saving source:', error);
    }
  };

  const handleDeleteSource = (sourceId) => {
    setSources(sources.filter(source => source.id !== sourceId));
    setShowDeleteConfirm(null);
  };

  const handleOpenWebSearchConfig = () => {
    setShowWebSearchConfigModal(true);
  };

  const handleSaveWebSearchConfig = async () => {
    try {
      const updatedConfig = await apiClient.updateWebSearchConfig(webSearchSettings);
      setWebSearchSettings(prev => ({
        ...prev,
        ...updatedConfig,
        search_engines: updatedConfig.search_engines || updatedConfig.engines || prev.search_engines
      }));
      setSources(prev => prev.map(s =>
        s.id === 'web_search'
          ? {
            ...s,
            isActive: !!updatedConfig.enabled,
            integrationStatus: updatedConfig.enabled ? 'active' : 'disabled',
            updatedAt: new Date().toISOString()
          }
          : s
      ));
      setShowWebSearchConfigModal(false);
    } catch (error) {
      console.error('Error saving Web Search configuration:', error);
      alert('Failed to save Web Search configuration: ' + error.message);
    }
  };

  const handleWebSearchEngineToggle = (engineId) => {
    setWebSearchSettings(prev => {
      const engines = prev.search_engines || [];
      const nextEngines = engines.includes(engineId)
        ? engines.filter(engine => engine !== engineId)
        : [...engines, engineId];
      return { ...prev, search_engines: nextEngines };
    });
  };

  const handleWebSearchMaxResultsChange = (value) => {
    const parsed = Number(value);
    const normalized = Number.isNaN(parsed) ? 1 : Math.max(1, Math.min(20, parsed));
    setWebSearchSettings(prev => ({ ...prev, max_results: normalized }));
  };

  const handleSaveRagConfig = async () => {
    setRagConfigSaveError(null);
    setSavingRagConfig(true);
    try {
      const editableKeys = [
        'parent_chunk_size',
        'parent_chunk_overlap',
        'chunk_size',
        'chunk_overlap',
        'top_k_results',
        'similarity_threshold'
      ];
      const payload = {};
      editableKeys.forEach(k => {
        if (ragConfigDraft[k] !== ragConfig[k]) {
          payload[k] = ragConfigDraft[k];
        }
      });
      if (Object.keys(payload).length === 0) {
        setSavingRagConfig(false);
        return;
      }
      await apiClient.updateRagConfig(payload);
      await loadRAGConfig();
      setRagConfigSavedFlash(true);
      setTimeout(() => setRagConfigSavedFlash(false), 2500);
    } catch (error) {
      console.error('Error saving RAG config:', error);
      setRagConfigSaveError(error?.message || 'Failed to save configuration');
    } finally {
      setSavingRagConfig(false);
    }
  };

  const handleToggleRAG = (source) => {
    // Toggle RAG enabled state
    const newRagState = !source.ragEnabled;

    setSources(sources.map(s =>
      s.id === source.id
        ? { ...s, ragEnabled: newRagState, updatedAt: new Date().toISOString() }
        : s
    ));

    // Persist to backend (and localStorage as cache)
    apiClient.updateRagEnabled(newRagState).catch((error) => {
      console.error('Error updating RAG enabled state:', error);
      // Revert UI on failure
      setSources(prev => prev.map(s =>
        s.id === source.id ? { ...s, ragEnabled: !newRagState, updatedAt: new Date().toISOString() } : s
      ));
    });
    localStorage.setItem('ragEnabled', JSON.stringify(newRagState));

    console.log(`RAG ${newRagState ? 'enabled' : 'disabled'} for Internal Knowledge Base`);
  };

  const loadRAGDocuments = async () => {
    try {
      // Load RAG documents from backend
      const documentsResponse = await apiClient.listRAGDocuments(uploadWorkspace);

      const documents = documentsResponse.documents || [];
      setRagDocuments(documents);

      // Load RAG statistics
      const statsResponse = await apiClient.request('/rag/statistics', {
        method: 'GET'
      });

      setRagStats(statsResponse);

      console.log('Loaded RAG documents:', documents.length);
      console.log('Documents:', documents.map(doc => ({
        id: doc.document_id,
        name: doc.filename,
        chunks: doc.total_chunks
      })));

      return documents;
    } catch (error) {
      console.error('Failed to load RAG documents:', error);

      // Check if RAG system is not available (503) or authentication failed
      const isRagUnavailable = error.message.includes('RAG system is not available') ||
        error.message.includes('503') ||
        error.message.includes('RAG system not initialized');
      const isAuthError = error.message.includes('Authentication') || error.message.includes('401');

      if (isRagUnavailable || isAuthError) {
        try {
          console.log('Trying public endpoint for RAG documents...');
          const publicResponse = await apiClient.request('/rag/documents/list', {
            headers: uploadWorkspace ? { 'x-workspace-id': uploadWorkspace } : {},
      method: 'GET'
          });
          if (publicResponse.ok) {
            const publicData = await publicResponse.json();
            const documents = publicData.documents || [];
            setRagDocuments(documents);

            // Get stats from health endpoint
            const healthResponse = await apiClient.request('/rag/health', {
              method: 'GET'
            });
            if (healthResponse.ok) {
              const healthData = await healthResponse.json();
              setRagStats({
                total_documents: healthData.total_documents,
                total_chunks: healthData.total_chunks,
                average_chunks_per_document: healthData.total_documents > 0 ? healthData.total_chunks / healthData.total_documents : 0,
                embedding_model: healthData.embedding_model
              });
            }

            console.log('Loaded RAG documents from public endpoint:', documents.length);
            return documents;
          }
        } catch (publicError) {
          console.error('Failed to load from public endpoint:', publicError);
        }
      }

      return [];
    }
  };

  const syncKnowledgeBaseSourceWithDocuments = (documents) => {
    setSources(prevSources => prevSources.map(source => {
      if (source.name !== 'Internal Knowledge Base') {
        return source;
      }

      return {
        ...source,
        lastSync: documents.length > 0 ? new Date().toISOString() : source.lastSync,
        updatedAt: new Date().toISOString(),
        uploadedFiles: documents.map(doc => ({
          id: doc.document_id,
          name: doc.filename,
          size: doc.file_size,
          type: doc.content_type,
          uploadDate: doc.upload_date,
          status: 'processed',
          chunks: doc.total_chunks
        }))
      };
    }));
  };

  const resetUploadState = () => {
    setUploadingFiles(false);
    setUploadPhase('idle');
    setSelectedUploadCount(0);
    setIsCancellingUpload(false);
    setUploadProgress({});
  };

  const isSupportedRAGFile = (file) => {
    const supportedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.md', '.rtf', '.csv'];
    const fileName = (file?.name || '').toLowerCase();
    return supportedExtensions.some(ext => fileName.endsWith(ext));
  };

  const uploadFiles = async (files, clearInput) => {
    const MIN_PREPARING_VISIBILITY_MS = 350;
    if (uploadAccessLevel === 'restricted' && selectedUploadGroups.length === 0) {
      alert('Please select at least one group for restricted access.');
      clearInput?.();
      resetUploadState();
      return;
    }

    if (files.length === 0) {
      clearInput?.();
      resetUploadState();
      return;
    }

    const supportedFiles = files.filter(isSupportedRAGFile);
    const skippedCount = files.length - supportedFiles.length;

    if (supportedFiles.length === 0) {
      alert('No supported files were selected. Allowed formats: PDF, DOC, DOCX, TXT, MD, RTF, CSV.');
      clearInput?.();
      resetUploadState();
      return;
    }

    if (skippedCount > 0) {
      alert(`${skippedCount} unsupported file(s) were skipped.`);
    }

    flushSync(() => {
      setUploadingFiles(true);
      setUploadPhase('preparing');
      setSelectedUploadCount(supportedFiles.length);
      setUploadProgress({});
      setIsCancellingUpload(false);
    });
    uploadControllersRef.current = [];
    const preparingStartedAt = Date.now();

    // Force one paint so users always see immediate feedback for large batches
    await new Promise(resolve => requestAnimationFrame(() => resolve()));
    const preparingElapsedMs = Date.now() - preparingStartedAt;
    if (preparingElapsedMs < MIN_PREPARING_VISIBILITY_MS) {
      await new Promise(resolve => setTimeout(resolve, MIN_PREPARING_VISIBILITY_MS - preparingElapsedMs));
    }

    const fileEntries = supportedFiles.map((file, index) => ({
      file,
      fileId: `${Date.now()}-${index}-${Math.random()}`,
      controller: new AbortController()
    }));
    uploadControllersRef.current = fileEntries.map(({ controller }) => controller);

    const initialUploadProgress = {};
    fileEntries.forEach(({ fileId }) => {
      // Mark as started while waiting for server ingestion
      initialUploadProgress[fileId] = 10;
    });

    // Apply phase switch + initial progress together to avoid visual gaps.
    flushSync(() => {
      setUploadPhase('uploading');
      setUploadProgress(initialUploadProgress);
    });

    const overridesActive = (
      chunkOverrides.parent_chunk_size !== ragConfig.parent_chunk_size ||
      chunkOverrides.parent_chunk_overlap !== ragConfig.parent_chunk_overlap ||
      chunkOverrides.child_chunk_size !== ragConfig.chunk_size ||
      chunkOverrides.child_chunk_overlap !== ragConfig.chunk_overlap
    );
    const chunkOptionsForUpload = overridesActive
      ? {
          parentChunkSize: Number(chunkOverrides.parent_chunk_size),
          parentChunkOverlap: Number(chunkOverrides.parent_chunk_overlap),
          childChunkSize: Number(chunkOverrides.child_chunk_size),
          childChunkOverlap: Number(chunkOverrides.child_chunk_overlap)
        }
      : null;

    const uploadResults = await Promise.allSettled(
      fileEntries.map(async ({ file, fileId, controller }) => {
        const result = await apiClient.uploadRAGDocument(
          file,
          'knowledge_base',
          uploadAccessLevel,
          selectedUploadGroups,
          uploadWorkspace,
          controller.signal,
          chunkOptionsForUpload
        );

        console.log('File uploaded to RAG system:', result);
        setUploadProgress(prev => ({ ...prev, [fileId]: 100 }));
        return { fileName: file.name };
      })
    );

    const failedUploads = uploadResults
      .map((result, index) => ({ result, fileName: fileEntries[index].file.name }))
      .filter(({ result }) => result.status === 'rejected')
      .filter(({ result }) => result.reason?.name !== 'AbortError')
      .map(({ result, fileName }) => ({
        fileName,
        error: result.reason
      }));
    const cancelledUploads = uploadResults
      .filter(result => result.status === 'rejected' && result.reason?.name === 'AbortError')
      .length;
    const hasFailedUploads = failedUploads.length > 0;

    if (hasFailedUploads) {
      const failureSummary = failedUploads
        .map(({ fileName, error }) => `${fileName}: ${error?.message || 'Unknown error'}`)
        .join('\n');
      alert(`Some files failed to upload:\n${failureSummary}`);
    }

    if (cancelledUploads > 0 && !hasFailedUploads) {
      alert(`Ingestion stopped. ${cancelledUploads} file upload(s) were cancelled.`);
    }

    try {
      // Refresh document list once after all uploads complete to avoid repeated page flicker
      const documents = await loadRAGDocuments();
      syncKnowledgeBaseSourceWithDocuments(documents);

      // Keep progress visible briefly at completion for smoother UX
      if (!hasFailedUploads && cancelledUploads === 0) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    } finally {
      uploadControllersRef.current = [];
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        fileEntries.forEach(({ fileId }) => {
          delete newProgress[fileId];
        });
        return newProgress;
      });
      setUploadingFiles(false);
      setUploadPhase('idle');
      setSelectedUploadCount(0);
      setIsCancellingUpload(false);
      clearInput?.();
    }
  };

  const handleFileUpload = async (event) => {
    pendingFileDialogRef.current = false;
    const files = Array.from(event.target.files || []);
    const inputElement = event.target;
    await uploadFiles(files, () => {
      if (inputElement) {
        inputElement.value = '';
      }
    });
  };

  const handleStopIngestion = () => {
    if (!uploadingFiles || isCancellingUpload) {
      return;
    }

    setIsCancellingUpload(true);
    uploadControllersRef.current.forEach((controller) => controller.abort());
  };

  const handleChooseFilesClick = () => {
    if (uploadingFiles) {
      return;
    }

    // Show preparing feedback while user is selecting files.
    setUploadingFiles(true);
    setUploadPhase('selecting');
    setSelectedUploadCount(0);
    setUploadProgress({});
    setIsCancellingUpload(false);
    pendingFileDialogRef.current = true;
    fileInputRef.current?.click();

    // If dialog is closed without selecting files and onChange does not fire,
    // reset state after a safe timeout window.
    const handleDialogClose = () => {
      const verifyDialogSelection = (attempt = 0) => {
        if (!pendingFileDialogRef.current) {
          return;
        }

        const hasFiles = (fileInputRef.current?.files?.length || 0) > 0;
        if (!hasFiles) {
          if (attempt < 80) {
            setTimeout(() => verifyDialogSelection(attempt + 1), 125);
            return;
          }
          resetUploadState();
        }
        pendingFileDialogRef.current = false;
      };

      setTimeout(() => verifyDialogSelection(0), 0);
    };
    window.addEventListener('focus', handleDialogClose, { once: true });
  };

  const handleUploadDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current += 1;
    setIsDragOverUpload(true);
  };

  const handleUploadDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleUploadDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) {
      setIsDragOverUpload(false);
    }
  };

  const handleUploadDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragOverUpload(false);

    if (uploadingFiles) {
      return;
    }

    const droppedFiles = Array.from(event.dataTransfer?.files || []);
    await uploadFiles(droppedFiles);
  };

  const handleDeleteFile = async (fileId) => {
    try {
      // Delete from RAG system
      await apiClient.request(`/rag/documents/${fileId}`, {
        method: 'DELETE'
      });

      console.log('Deleted document from RAG system:', fileId);

      // Reload RAG documents and refresh sources
      const documents = await loadRAGDocuments();
      syncKnowledgeBaseSourceWithDocuments(documents);
    } catch (error) {
      console.error('Error deleting file:', error);
      alert(`Failed to delete file: ${error.message}`);
    }
  };

  const handleDeleteAllFiles = async () => {
    if (ragDocuments.length === 0 || deletingAllFiles) {
      return;
    }

    const confirmed = window.confirm(
      `Delete all ${ragDocuments.length} uploaded documents? This action cannot be undone.`
    );
    if (!confirmed) {
      return;
    }

    setDeletingAllFiles(true);
    try {
      const deletionResults = await Promise.allSettled(
        ragDocuments.map((doc) =>
          apiClient.request(`/rag/documents/${doc.document_id}`, {
            method: 'DELETE'
          })
        )
      );

      const failedCount = deletionResults.filter(result => result.status === 'rejected').length;
      if (failedCount > 0) {
        alert(`Failed to delete ${failedCount} document(s). Please try again.`);
      }

      const documents = await loadRAGDocuments();
      syncKnowledgeBaseSourceWithDocuments(documents);
    } catch (error) {
      console.error('Error deleting all files:', error);
      alert(`Failed to delete all files: ${error.message}`);
    } finally {
      setDeletingAllFiles(false);
    }
  };

  const handleCustomerDatabaseSave = (config) => {
    // Update the Customer Database source with the new configuration
    setSources(sources.map(source =>
      source.name === 'Customer Database'
        ? {
          ...source,
          ...config,
          integrationStatus: 'configured',
          isActive: true,
          updatedAt: new Date().toISOString()
        }
        : source
    ));

    // Close modal and reset state
    setShowCustomerDatabaseModal(false);
    setCustomerDatabaseConfig(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileType) => {
    if (fileType.includes('pdf')) return 'fas fa-file-pdf';
    if (fileType.includes('word') || fileType.includes('document')) return 'fas fa-file-word';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return 'fas fa-file-excel';
    if (fileType.includes('powerpoint') || fileType.includes('presentation')) return 'fas fa-file-powerpoint';
    if (fileType.includes('image')) return 'fas fa-file-image';
    if (fileType.includes('text') || fileType.includes('csv')) return 'fas fa-file-alt';
    return 'fas fa-file';
  };

  const filteredSources = sources.filter(source => {
    const matchesSearch = source.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      source.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      source.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesType = filterType === 'all' || source.type === filterType;
    return matchesSearch && matchesType;
  });

  const getSourceTypeIcon = (type) => {
    const sourceType = sourceTypes.find(st => st.value === type);
    return sourceType ? sourceType.icon : 'fas fa-question';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  // Helper function to determine badge variant based on source status
  const getStatusBadge = (source) => {
    if (source.isActive || source.integrationStatus === 'active' || source.integrationStatus === 'configured') {
      return { variant: 'success', text: 'Active' };
    }
    if (source.integrationStatus === 'available') {
      return { variant: 'primary', text: 'Available' };
    }
    if (source.integrationStatus === 'disabled') {
      return { variant: 'neutral', text: 'Disabled' };
    }
    return { variant: 'neutral', text: 'Configured' };
  };

  const uploadProgressValues = Object.values(uploadProgress);
  const activeUploadCount = uploadProgressValues.length;
  const uploadProgressPercent = activeUploadCount > 0
    ? Math.round(uploadProgressValues.reduce((sum, value) => sum + value, 0) / activeUploadCount)
    : 0;
  const completedUploadCount = uploadProgressValues.filter(value => value >= 100).length;
  const displayedUploadCount = activeUploadCount || selectedUploadCount;
  const isPreUploadPhase = uploadPhase === 'selecting' || uploadPhase === 'preparing';
  const displayUploadPercent = isPreUploadPhase ? 0 : uploadProgressPercent;
  const displayCompletedCount = isPreUploadPhase ? 0 : completedUploadCount;

  // Overrides are "active" whenever any value differs from server defaults.
  // Collapsing the panel does not deactivate them — values follow the form,
  // not the panel's open/closed state.
  const isChunkOverrideActive = (
    chunkOverrides.parent_chunk_size !== ragConfig.parent_chunk_size ||
    chunkOverrides.parent_chunk_overlap !== ragConfig.parent_chunk_overlap ||
    chunkOverrides.child_chunk_size !== ragConfig.chunk_size ||
    chunkOverrides.child_chunk_overlap !== ragConfig.chunk_overlap
  );

  if (loading) {
    return (
      <div className="page-container information-sources">
        <div className="page-content">
          <div className="section-content">
            <p>Loading data sources...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container information-sources">
      <div className="page-content">
        <PageHeader
          title="Data Sources"
          description="Manage data sources and knowledge bases for your organization"
          icon="fas fa-database"
          accentColor="blue"
        />

      {/* Organization Context Section */}
      <div className="context-category">
        <div className="category-header">
          <div className="category-title">
            <div>
              <h3>
                Organization Context
                <span className="category-badge shared">
                  Organization-Wide
                </span>
              </h3>
              <p>Common organizational knowledge shared across all users</p>
            </div>
          </div>
        </div>

        <div className="sources-grid">
          {/* Internal Knowledge Base */}
          {isSourceVisible('internal_kb') && filteredSources.filter(source => source.name === 'Internal Knowledge Base').map(source => {
            const statusBadge = getStatusBadge(source);
            return (
              <div key={source.id} className="source-card">
                <div className="source-header">
                  <i className={`${getSourceTypeIcon(source.type)} source-icon`}></i>
                  <Badge variant={statusBadge.variant}>{statusBadge.text}</Badge>
                </div>
                <div className="source-content">
                  <h3>{source.name}</h3>
                  <p>{source.description}</p>
                </div>
                <div className="source-footer">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="full-width"
                    onClick={() => setShowRAGConfigModal(true)}
                  >
                    Configure
                  </Button>
                </div>
              </div>
            );
          })}

          {/* Amazon Bedrock Knowledge Base */}
          {isSourceVisible('bedrock_kb') && (
            <div className="source-card">
              <div className="source-header">
                <i className="fab fa-aws source-icon"></i>
                <Badge variant={bedrockConfig.is_configured ? 'success' : 'neutral'}>
                  {bedrockConfig.is_configured ? 'Configured' : 'Available'}
                </Badge>
              </div>
              <div className="source-content">
                <h3>Amazon Bedrock Knowledge Base</h3>
                <p>Enterprise knowledge base powered by AWS Bedrock</p>
              </div>
              <div className="source-footer">
                <Button
                  variant="secondary"
                  size="sm"
                  className="full-width"
                  onClick={() => setShowKnowledgeBaseModal(true)}
                >
                  Configure
                </Button>
              </div>
            </div>
          )}

          {/* CLabS Source Store */}
          {isSourceVisible('clabs_source') && (
            <div className="source-card">
              <div className="source-header">
                <i className={`${getSourceTypeIcon(clabsSystemSource.type)} source-icon`}></i>
                <Badge variant={clabsSystemSource.isActive ? 'success' : 'neutral'}>
                  {clabsSystemSource.isActive ? 'Active' : 'Available'}
                </Badge>
              </div>
              <div className="source-content">
                <h3>Clabs Source</h3>
                <p>{clabsSystemSource.description}</p>
              </div>
              <div className="source-footer">
                <Button
                  variant="secondary"
                  size="sm"
                  className="full-width"
                  onClick={() => handleEditSource(clabsSystemSource)}
                >
                  Configure
                </Button>
              </div>
            </div>
          )}

          {/* Customer Database and other sources */}
          {filteredSources.filter(source => source.name !== 'Internal Knowledge Base').map(source => {
            if (!isSourceVisible(source.id) && source.id !== 'web_search') {
              return null;
            }

            const statusBadge = getStatusBadge(source);
            
            return (
              <div key={source.id} className="source-card">
                <div className="source-header">
                  <i className={`${getSourceTypeIcon(source.type)} source-icon`}></i>
                  <Badge variant={statusBadge.variant}>{statusBadge.text}</Badge>
                </div>
                <div className="source-content">
                  <h3>{source.name}</h3>
                  <p>{source.description}</p>
                </div>
                <div className="source-footer">
                  {source.id === 'web_search' ? (
                    <Button
                      variant={source.isActive ? 'primary' : 'secondary'}
                      size="sm"
                      className="full-width"
                      onClick={handleOpenWebSearchConfig}
                    >
                      Manage
                    </Button>
                  ) : source.isCorporateIntegration ? (
                    source.name === 'Customer Database' && source.integrationStatus === 'available' ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        className="full-width"
                        onClick={() => {
                          setCustomerDatabaseConfig(source);
                          setShowCustomerDatabaseModal(true);
                        }}
                      >
                        Configure
                      </Button>
                    ) : null
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="full-width"
                      onClick={() => handleEditSource(source)}
                    >
                      Configure
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Personal Context Section */}
      <div className="context-category private-context">
        <div className="category-header">
          <div className="category-title">
            <div>
              <h3>
                Personal Context
                <span className="category-badge private">
                  Private
                </span>
              </h3>
              <p>Your personal conversation history - <strong>only you can see and use this data</strong></p>
            </div>
          </div>
        </div>

        <div className="sources-grid">
          {/* Personal Context */}
          {isSourceVisible('personal_context') && (
            <div className="source-card">
              <div className="source-header">
                <i className="fas fa-user-lock source-icon"></i>
                <Badge variant="outline" icon="fas fa-lock">Private</Badge>
              </div>
              <div className="source-content">
                <h3>Personal Context</h3>
                <p>Import and use your conversation history from ChatGPT, Claude, or other AI assistants. No other user in your organization can access or see this data.</p>
              </div>
              <div className="source-footer">
                <Button
                  variant="secondary"
                  size="sm"
                  className="full-width"
                  onClick={() => setShowPersonalContextModal(true)}
                >
                  Configure
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {filteredSources.length === 0 && (
        <div className="empty-state">
          <i className="fas fa-database"></i>
          <h3>No data sources found</h3>
          <p>Create your first data source to get started</p>
          <button className="btn btn-primary" onClick={handleCreateSource}>
            <i className="fas fa-plus"></i>
            Add Source
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      <PortalModal
        isOpen={isCreating || !!editingSource}
        onClose={() => {
          setIsCreating(false);
          setEditingSource(null);
        }}
        title={editingSource ? 'Edit Source' : 'Add New Source'}
        footer={
          <>
            <Button 
              variant="ghost" 
              onClick={() => {
                setIsCreating(false);
                setEditingSource(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveSource}
              disabled={!formData.name || !formData.type}
            >
              {editingSource ? 'Update Source' : 'Create Source'}
            </Button>
          </>
        }
      >
        <div className="form-grid">
          <Input
            label="Source Name *"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter source name"
          />

          <Select
            label="Source Type *"
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            options={sourceTypes}
          />

          <Select
            label="Access Level *"
            value={formData.accessLevel}
            onChange={(e) => setFormData({ ...formData, accessLevel: e.target.value })}
            options={accessLevels}
          />

          <Select
            label="Sync Frequency"
            value={formData.syncFrequency}
            onChange={(e) => setFormData({ ...formData, syncFrequency: e.target.value })}
            options={syncFrequencies}
          />

          <div className="ui-form-group">
            <label className="ui-label">Description</label>
            <textarea
              className="ui-input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe this data source"
              rows="3"
            />
          </div>

          {formData.type === 'database' && (
            <Input
              label="Connection String"
              value={formData.connectionString}
              onChange={(e) => setFormData({ ...formData, connectionString: e.target.value })}
              placeholder="Database connection string"
              icon="fas fa-database"
            />
          )}

          {formData.type === 'api' && (
            <Input
              label="API Endpoint"
              value={formData.apiEndpoint}
              onChange={(e) => setFormData({ ...formData, apiEndpoint: e.target.value })}
              placeholder="API endpoint URL"
              icon="fas fa-plug"
            />
          )}

          {formData.type === 'clabs' && (
            <div className="clabs-redirect-message">
              <i className="fas fa-info-circle"></i>
              <p>Clabs sources require specialized configuration. Please use the dedicated Clabs configuration modal.</p>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setIsCreating(false);
                  setEditingSource(null);
                  handleCreateClabsSource();
                }}
                icon="fas fa-brain"
              >
                Configure Clabs Source
              </Button>
            </div>
          )}

          <Input
            label="Tags (comma-separated)"
            value={formData.tags.join(', ')}
            onChange={(e) => setFormData({
              ...formData,
              tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
            })}
            placeholder="Enter tags separated by commas"
          />
        </div>
      </PortalModal>


      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this data source? This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={() => handleDeleteSource(showDeleteConfirm)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Knowledge Base Modal */}
      {showKnowledgeBaseModal && (
        <KnowledgeBaseModal
          isOpen={showKnowledgeBaseModal}
          onClose={() => setShowKnowledgeBaseModal(false)}
          onConfigUpdate={loadBedrockConfig}
        />
      )}

      {/* Clabs Configuration Modal */}
      <ClabsModal
        isOpen={showClabsModal}
        onClose={() => setShowClabsModal(false)}
        onSave={handleSaveClabsConfiguration}
        initialConfig={editingClabsSource}
      />


      {/* Customer Database Modal */}
      {showCustomerDatabaseModal && (
        <CustomerDatabaseModal
          isOpen={showCustomerDatabaseModal}
          onClose={() => {
            setShowCustomerDatabaseModal(false);
            setCustomerDatabaseConfig(null);
          }}
          onSave={handleCustomerDatabaseSave}
          existingConfig={customerDatabaseConfig}
        />
      )}

      {/* Web Search Configuration Modal */}
      <PortalModal
        isOpen={showWebSearchConfigModal}
        onClose={() => setShowWebSearchConfigModal(false)}
        title="Web Search Configuration"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowWebSearchConfigModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleSaveWebSearchConfig} disabled={!webSearchSettings.url}>Save Configuration</Button>
          </>
        }
      >
        <div className="form-grid">
          <Input
            label="SearXNG Base URL"
            value={webSearchSettings.url}
            onChange={(e) => setWebSearchSettings(prev => ({ ...prev, url: e.target.value }))}
            placeholder="http://localhost:8080"
            helperText="Enter a URL reachable by the backend for your SearXNG instance."
          />

          <div className="ui-form-group">
            <label className="ui-label">Search Engines</label>
            <div className="engines-grid">
              {webSearchEngineOptions.map((engine) => {
                const isSelected = webSearchSettings.search_engines.includes(engine.id);
                return (
                  <div
                    key={engine.id}
                    className={`engine-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleWebSearchEngineToggle(engine.id)}
                  >
                    {isSelected && <i className="fas fa-check"></i>}
                    <span>{engine.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <Select
              label="Language"
              value={webSearchSettings.language}
              onChange={(e) => setWebSearchSettings(prev => ({ ...prev, language: e.target.value }))}
              options={[
                { value: 'all', label: 'All' },
                { value: 'en', label: 'English' },
                { value: 'es', label: 'Spanish' }
              ]}
            />
            
            <Input
              label="Max Results"
              type="number"
              min="1"
              max="20"
              value={webSearchSettings.max_results}
              onChange={(e) => handleWebSearchMaxResultsChange(e.target.value)}
            />
          </div>

          <div className="ui-form-group" style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', background: 'var(--bg-surface)', padding: '1rem', borderRadius: '8px', border: '1px solid var(--border-subtle)' }}>
            <div>
              <label className="ui-label" style={{ marginBottom: 0, color: 'var(--text-primary)' }}>Deep Research Mode</label>
              <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>Uses AI Agent to read full page content</p>
            </div>
            <ToggleSwitch
              checked={webSearchSettings.deep_research_mode}
              onChange={(v) => setWebSearchSettings(prev => ({ ...prev, deep_research_mode: v }))}
            />
          </div>
        </div>
      </PortalModal>


      {/* Personal Context Modal */}
      <PersonalContextModal
        isOpen={showPersonalContextModal}
        onClose={() => setShowPersonalContextModal(false)}
      />

      {/* RAG Configuration Modal */}
      <PortalModal
        isOpen={showRAGConfigModal}
        onClose={() => setShowRAGConfigModal(false)}
        title="Internal Knowledge Base"
        size="large"
        footer={
          <Button variant="secondary" onClick={() => setShowRAGConfigModal(false)}>Close</Button>
        }
      >
        {/* Tabs */}
        <div className="rag-tabs">
          <button
            className={`rag-tab-btn ${ragConfigTab === 'files' ? 'active' : ''}`}
            onClick={() => setRagConfigTab('files')}
          >
            <i className="fas fa-folder-open"></i> File Management
          </button>
          <button
            className={`rag-tab-btn ${ragConfigTab === 'settings' ? 'active' : ''}`}
            onClick={() => setRagConfigTab('settings')}
          >
            <i className="fas fa-cog"></i> Configuration
          </button>
        </div>

        {/* Files Tab */}
        {ragConfigTab === 'files' && (
          <div className="rag-section">
            {/* Enable/Disable Toggle */}
            <div className="ui-form-group" style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '1.5rem', borderBottom: '1px solid var(--border-subtle)' }}>
              <div>
                <h4 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)' }}>RAG System Status</h4>
                <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Enable to use documents for AI responses</p>
              </div>
              <ToggleSwitch
                checked={sources.find(s => s.name === 'Internal Knowledge Base')?.ragEnabled || false}
                onChange={() => {
                  const source = sources.find(s => s.name === 'Internal Knowledge Base');
                  if (source) handleToggleRAG(source);
                }}
              />
            </div>

            <div className="ui-form-group" style={{ marginTop: '1rem' }}>
              <Select
                label="Upload to Workspace"
                value={uploadWorkspace}
                onChange={(e) => setUploadWorkspace(e.target.value)}
                options={[
                  { value: 'shared', label: 'Shared (Organization-wide)' },
                  ...workspaces.map(ws => ({ value: ws._id || ws.id, label: ws.name }))
                ]}
              />
            </div>

            {/* Advanced chunking overrides */}
            <div className="rag-chunk-overrides">
              <button
                type="button"
                className="rag-chunk-overrides-toggle"
                onClick={() => setChunkOverridesOpen(prev => !prev)}
                aria-expanded={chunkOverridesOpen}
              >
                <i className={`fas fa-chevron-${chunkOverridesOpen ? 'down' : 'right'}`}></i>
                <span>Advanced chunking</span>
                {isChunkOverrideActive && (
                  <span className="rag-chunk-overrides-badge">Active</span>
                )}
                <span className="rag-chunk-overrides-summary">
                  {isChunkOverrideActive
                    ? `parent ${chunkOverrides.parent_chunk_size}/${chunkOverrides.parent_chunk_overlap} · child ${chunkOverrides.child_chunk_size}/${chunkOverrides.child_chunk_overlap}`
                    : 'Using server defaults'}
                </span>
              </button>

              {chunkOverridesOpen && (
                <div className="rag-chunk-overrides-body">
                  <p className="rag-chunk-overrides-help">
                    Overrides how the next upload(s) are split into parent and child chunks.
                    Children are embedded for retrieval; the matching child&apos;s parent is what the LLM receives.
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <Input
                      label="Parent chunk size"
                      type="number"
                      min="100"
                      max="20000"
                      value={chunkOverrides.parent_chunk_size}
                      onChange={(e) => setChunkOverrides(prev => ({ ...prev, parent_chunk_size: parseInt(e.target.value, 10) || 0 }))}
                      helperText="Characters per parent passage"
                    />
                    <Input
                      label="Parent chunk overlap"
                      type="number"
                      min="0"
                      max="5000"
                      value={chunkOverrides.parent_chunk_overlap}
                      onChange={(e) => setChunkOverrides(prev => ({ ...prev, parent_chunk_overlap: parseInt(e.target.value, 10) || 0 }))}
                      helperText="Characters carried into the next parent"
                    />
                    <Input
                      label="Child chunk size"
                      type="number"
                      min="50"
                      max="4000"
                      value={chunkOverrides.child_chunk_size}
                      onChange={(e) => setChunkOverrides(prev => ({ ...prev, child_chunk_size: parseInt(e.target.value, 10) || 0 }))}
                      helperText="Characters per embedded child"
                    />
                    <Input
                      label="Child chunk overlap"
                      type="number"
                      min="0"
                      max="2000"
                      value={chunkOverrides.child_chunk_overlap}
                      onChange={(e) => setChunkOverrides(prev => ({ ...prev, child_chunk_overlap: parseInt(e.target.value, 10) || 0 }))}
                      helperText="Characters carried into the next child"
                    />
                  </div>
                  <button
                    type="button"
                    className="rag-chunk-overrides-reset"
                    onClick={() => setChunkOverrides({
                      parent_chunk_size: ragConfig.parent_chunk_size,
                      parent_chunk_overlap: ragConfig.parent_chunk_overlap,
                      child_chunk_size: ragConfig.chunk_size,
                      child_chunk_overlap: ragConfig.chunk_overlap
                    })}
                  >
                    Reset to server defaults
                  </button>
                </div>
              )}
            </div>

            {/* Upload Area */}
            <div
              className={`upload-dropzone ${isDragOverUpload ? 'drag-over' : ''}`}
              onDragEnter={handleUploadDragEnter}
              onDragOver={handleUploadDragOver}
              onDragLeave={handleUploadDragLeave}
              onDrop={handleUploadDrop}
            >
              <i className="fas fa-cloud-upload-alt upload-icon"></i>
              <h4>Upload Documents</h4>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                Drag & drop or click to browse (PDF, DOC, DOCX, TXT, MD, RTF, CSV)
              </p>
              <input
                type="file"
                id="rag-file-upload"
                ref={fileInputRef}
                multiple
                accept=".pdf,.doc,.docx,.txt,.md,.rtf,.csv"
                onChange={(e) => {
                  const source = sources.find(s => s.name === 'Internal Knowledge Base');
                  if (source) handleFileUpload(e);
                }}
                style={{ display: 'none' }}
              />
              <Button 
                variant="primary" 
                size="sm" 
                disabled={uploadingFiles}
                onClick={handleChooseFilesClick}
                icon="fas fa-folder-open"
              >
                {uploadingFiles ? 'Uploading...' : 'Choose Files'}
              </Button>
            </div>

            {uploadingFiles && (displayedUploadCount > 0 || isPreUploadPhase) && (
              <div className="rag-upload-progress">
                <div className="rag-upload-progress-header">
                  <span>{isPreUploadPhase ? 'Preparing files...' : 'Ingesting files...'}</span>
                  <div className="rag-upload-progress-actions">
                    {!isPreUploadPhase && (
                      <span>{displayCompletedCount}/{Math.max(displayedUploadCount, 1)} completed</span>
                    )}
                    <Button
                      variant="danger"
                      size="sm"
                      icon="fas fa-stop"
                      onClick={handleStopIngestion}
                      disabled={isCancellingUpload}
                    >
                      {isCancellingUpload ? 'Stopping...' : 'Stop Ingestion'}
                    </Button>
                  </div>
                </div>
                <div className="rag-upload-progress-track">
                  <div
                    className={`rag-upload-progress-fill ${isPreUploadPhase ? 'indeterminate' : ''}`}
                    style={{ width: isPreUploadPhase ? '35%' : `${displayUploadPercent}%` }}
                  />
                </div>
                <span className="rag-upload-progress-percent">
                  {isPreUploadPhase ? 'Preparing upload...' : `${displayUploadPercent}%`}
                </span>
              </div>
            )}

            {/* Document List */}
            <div className="section-header-with-action" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem' }}>
              <h4 style={{ fontSize: '0.95rem', color: 'var(--text-secondary)' }}>Uploaded Documents ({ragDocuments.length})</h4>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  icon="fas fa-sync" 
                  disabled={deletingAllFiles}
                  onClick={async () => {
                    const documents = await loadRAGDocuments();
                    syncKnowledgeBaseSourceWithDocuments(documents);
                  }}
                />
                <Button
                  variant="danger"
                  size="sm"
                  icon="fas fa-trash-alt"
                  disabled={ragDocuments.length === 0 || deletingAllFiles || uploadingFiles}
                  onClick={handleDeleteAllFiles}
                >
                  {deletingAllFiles ? 'Deleting...' : 'Delete All'}
                </Button>
              </div>
            </div>

            <div className="document-list">
              {ragDocuments.length > 0 ? ragDocuments.map(file => (
                <div key={file.document_id} className="document-item">
                  <i className={`${getFileIcon(file.content_type)} doc-icon`}></i>
                  <div className="doc-info">
                    <span className="doc-name">{file.filename}</span>
                    <span className="doc-meta">
                      {formatFileSize(file.file_size)} • {formatDate(file.upload_date)}
                      {file.total_chunks && ` • ${file.total_chunks} chunks`}
                    </span>
                  </div>
                  <Button
                    variant="danger"
                    size="sm"
                    icon="fas fa-trash"
                    onClick={() => {
                      const source = sources.find(s => s.name === 'Internal Knowledge Base');
                      if (source) handleDeleteFile(file.document_id);
                    }}
                  />
                </div>
              )) : (
                <div className="empty-state" style={{ padding: '2rem' }}>
                  <i className="fas fa-inbox"></i>
                  <p>No documents uploaded yet.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {ragConfigTab === 'settings' && (() => {
          const editableKeys = [
            'parent_chunk_size',
            'parent_chunk_overlap',
            'chunk_size',
            'chunk_overlap',
            'top_k_results',
            'similarity_threshold'
          ];
          const isRagConfigDirty = editableKeys.some(k => ragConfigDraft[k] !== ragConfig[k]);
          const updateDraft = (k, v) => setRagConfigDraft(prev => ({ ...prev, [k]: v }));
          const parseInt10 = (v) => {
            const n = parseInt(v, 10);
            return Number.isFinite(n) ? n : 0;
          };
          const parseFloat1 = (v) => {
            const n = parseFloat(v);
            return Number.isFinite(n) ? n : 0;
          };
          return (
          <div className="rag-section">
            <div className="rag-settings-callout">
              <i className="fas fa-info-circle"></i>
              <div>
                <strong>These settings apply to everyone in the organization.</strong>
                <p>
                  Changes take effect immediately, but only new uploads are
                  affected — files already in the knowledge base keep the
                  settings they were uploaded with. Need different values for
                  a single file? Use <em>Advanced chunking</em> on the Files tab.
                </p>
              </div>
            </div>

            <div className="form-grid">
              <h4 className="rag-settings-section-h">Ingestion (chunking)</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                <Input
                  label="Parent Chunk Size"
                  type="number"
                  min="100"
                  max="20000"
                  value={ragConfigDraft.parent_chunk_size}
                  onChange={(e) => updateDraft('parent_chunk_size', parseInt10(e.target.value))}
                  helperText="RAG_PARENT_CHUNK_SIZE — characters per parent passage."
                />
                <Input
                  label="Parent Chunk Overlap"
                  type="number"
                  min="0"
                  max="5000"
                  value={ragConfigDraft.parent_chunk_overlap}
                  onChange={(e) => updateDraft('parent_chunk_overlap', parseInt10(e.target.value))}
                  helperText="RAG_PARENT_CHUNK_OVERLAP — chars carried into the next parent."
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                <Input
                  label="Child Chunk Size"
                  type="number"
                  min="50"
                  max="4000"
                  value={ragConfigDraft.chunk_size}
                  onChange={(e) => updateDraft('chunk_size', parseInt10(e.target.value))}
                  helperText="RAG_CHUNK_SIZE — characters per embedded child."
                />
                <Input
                  label="Child Chunk Overlap"
                  type="number"
                  min="0"
                  max="2000"
                  value={ragConfigDraft.chunk_overlap}
                  onChange={(e) => updateDraft('chunk_overlap', parseInt10(e.target.value))}
                  helperText="RAG_CHUNK_OVERLAP — chars carried into the next child."
                />
              </div>

              <h4 className="rag-settings-section-h">Retrieval</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                <Input
                  label="Top K Results"
                  type="number"
                  min="1"
                  max="50"
                  value={ragConfigDraft.top_k_results}
                  onChange={(e) => updateDraft('top_k_results', parseInt10(e.target.value))}
                  helperText="RAG_TOP_K_RESULTS — parents returned per query."
                />
                <Input
                  label="Similarity Threshold"
                  type="number"
                  step="0.05"
                  min="0"
                  max="1"
                  value={ragConfigDraft.similarity_threshold}
                  onChange={(e) => updateDraft('similarity_threshold', parseFloat1(e.target.value))}
                  helperText="RAG_SIMILARITY_THRESHOLD — min cosine score."
                />
              </div>

              <h4 className="rag-settings-section-h">Embeddings</h4>
              <Select
                label="Embedding Model"
                value={ragConfig.embedding_model}
                disabled={true}
                options={[{ value: ragConfig.embedding_model, label: ragConfig.embedding_model }]}
                helperText="EMBEDDING_MODEL — restart required to change."
              />
            </div>

            <div className="rag-config-save-row">
              <div className="rag-config-save-status">
                {ragConfigSaveError && (
                  <span className="rag-config-save-error">
                    <i className="fas fa-exclamation-triangle"></i> {ragConfigSaveError}
                  </span>
                )}
                {!ragConfigSaveError && ragConfigSavedFlash && (
                  <span className="rag-config-save-ok">
                    <i className="fas fa-check"></i> Saved and applied.
                  </span>
                )}
                {!ragConfigSaveError && !ragConfigSavedFlash && isRagConfigDirty && (
                  <span className="rag-config-save-dirty">Unsaved changes</span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!isRagConfigDirty || savingRagConfig}
                  onClick={() => setRagConfigDraft(ragConfig)}
                >
                  Discard
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!isRagConfigDirty || savingRagConfig}
                  onClick={handleSaveRagConfig}
                  icon={savingRagConfig ? 'fas fa-spinner fa-spin' : 'fas fa-save'}
                >
                  {savingRagConfig ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </div>

            <div className="stats-grid" style={{ marginTop: '2rem' }}>
               <div className="stat-card">
                  <span className="stat-val">{ragStats.total_documents}</span>
                  <span className="stat-label">Documents</span>
               </div>
               <div className="stat-card">
                  <span className="stat-val">{ragStats.total_chunks}</span>
                  <span className="stat-label">Total Chunks</span>
               </div>
               <div className="stat-card">
                  <span className="stat-val">{ragStats.average_chunks_per_document.toFixed(1)}</span>
                  <span className="stat-label">Avg Chunks/Doc</span>
               </div>
            </div>
          </div>
          );
        })()}
      </PortalModal>

      </div>
    </div>
  );
}

